import SaveScriptBtn from "@pages/aiStudio/components/SaveScriptBtn";
import { Segmented, Switch } from "antd";
import { Trans } from "react-i18next";
import { IconClose } from "@icons/index";
import { Popover } from "antd";
import { useOpenWindow } from "@utils/window";
import classNames from "classnames";
import { useState, useEffect } from "react";
import { useAppSelector } from "@hooks/redux";
import { CommonButton } from "@components/CommonButton";
import Image from "@components/Image";
import { formatImgUrl } from "@utils/format";
import report from "@report";
import { PAGES } from "@routes";
import { useSearchParams } from "react-router-dom";
import { useAiStudio } from "@pages/aiStudio/hooks";
import { VideoCreationModal } from "../VideoCreationModal";
import StoryboardTable from "../StoryboardTable";
import "./index.scss";
import VideoAnalysisPanel from "../VideoAnalysisPanel";
import { CreditGuardButton } from "@components/CreditGuardButton";
import { TestId } from "@shared/testIds";

interface VideoScriptPanelProps {
  title?: string;
  isTranslateLoading?: boolean;
  showProduct?: boolean;
  className?: string;
  data?: any;
  scriptText?: any;
  isPlaying?: boolean;
  status: Status;
  handleTrans?: (value: boolean) => void;
  handleCurTime?: (time: number) => void;
  handleTranslate?: (translate: boolean, index: number) => void;
  handleSave?: (data: any) => void;
  onParseProductFailed?: () => void;
}

export const VideoScriptPanel = ({
  isTranslateLoading,
  showProduct,
  className,
  data,
  scriptText,
  isPlaying,
  status,
  handleCurTime,
  handleTranslate,
  handleSave,
  onParseProductFailed,
}: VideoScriptPanelProps) => {
  const { downloadVideoScriptAnalysis, downloadVideoScriptGenerate, downloadStatus } = useAiStudio();
  const { language } = useAppSelector((state) => state.globalReducer);
  const { openWindow } = useOpenWindow();
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get("id");
  const videoSource = searchParams.get("videoSource");
  const country = searchParams.get("country");

  const [showType, setShowType] = useState(0);
  const [activeScript, setActiveScript] = useState<number>(0);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [showVideoCreationModal, setShowVideoCreationModal] = useState(false);
  const HiddenVideoScriptTooltip = Boolean(localStorage.getItem("HiddenVideoScriptTooltip")) || false;

  useEffect(() => {
    if (data.length === 2 && !HiddenVideoScriptTooltip) setTooltipOpen(true);
    if (data.length === 4 && activeScript !== 3) setActiveScript(2);
  }, [data.length]);

  // “Two scripts ready” 提示 10 秒后自动关闭
  useEffect(() => {
    if (!tooltipOpen) return;
    const timer = setTimeout(() => {
      setTooltipOpen(false);
      localStorage.setItem("HiddenVideoScriptTooltip", "true");
    }, 10 * 1000);
    return () => clearTimeout(timer);
  }, [tooltipOpen]);

  const renderSegmentedOptions = () => {
    const options = data.map((_: any, idx: number) => ({
      value: idx,
      label: (
        <div className={classNames("flex items-center gap-[2px]", activeScript === idx && "font-semibold")}>
          <Trans i18nKey={"script_no"} values={{ script_no: idx + 1 }} defaults="Script {{script_no}}"></Trans>
        </div>
      ),
    }));
    if (data.length === 2 && status === "loading" && !isTranslateLoading) {
      options.push({
        value: 2,
        disabled: true,
        label: (
          <div className="flex items-center gap-[4px]">
            <svg className="animate-spin" width="20" height="20" viewBox="0 0 18 18" fill="none">
              <path d="M16 9A7 7 0 1 1 9 2" stroke="#bbb" strokeWidth="1.23" strokeLinecap="round" />
            </svg>
            <Trans>Loading</Trans>
          </div>
        ),
      });
    }
    return options;
  };

  const renderExportScriptText = () => {
    if (data.length > 1)
      return (
        <div className="flex items-center gap-[4px]">
          <span>
            <Trans i18nKey={"script_export"}>Export</Trans>
          </span>
          [<Trans i18nKey="script_no" values={{ script_no: activeScript + 1 }} defaults="Script {{script_no}}" />]
        </div>
      );
    return <Trans>Export Script</Trans>;
  };

  return (
    <div className={classNames("VideoScriptPanel flex flex-col w-full h-full min-w-0", className)}>
      {/* 顶部tab栏 */}
      <div className="flex flex-row gap-[16px] items-center justify-between px-[16px] h-[53px] border-b border-kds-border">
        {data.length > 1 && (
          <Popover
            open={tooltipOpen}
            content={
              <div className="relative w-[170px] py-[8px] pl-[12px] pr-[32px]">
                <Trans i18nKey="two_scripts_ready">Two scripts ready! Click to switch.</Trans>
                <IconClose
                  className="absolute top-[8px] right-[8px] cursor-pointer"
                  onClick={() => {
                    setTooltipOpen(false);
                    localStorage.setItem("HiddenVideoScriptTooltip", "true");
                  }}
                />
              </div>
            }
          >
            <Segmented
              shape="round"
              value={activeScript}
              onChange={setActiveScript}
              options={renderSegmentedOptions()}
              disabled={isTranslateLoading || status === "loading"}
            />
          </Popover>
        )}
        {location.pathname === "/insight" && (
          <Segmented
            shape="round"
            value={showType}
            onChange={setShowType}
            options={[
              {
                value: 0,
                label: <Trans>Video Script</Trans>,
              },
              {
                value: 1,
                label: <Trans>Key to Success</Trans>,
              },
            ]}
          />
        )}
        <div className="flex-1"></div>
        {/* 翻译开关 */}
        {data[activeScript]?.language_code !== language && (
          <div className="flex flex-row gap-1 items-center">
            <div className="font-normal text-kds-fg text-[16px]">
              <Trans>Translate</Trans>
            </div>
            <Switch
              value={data[activeScript]?.translate}
              onChange={(e) => {
                report.event("script_translate", { kalo_type: String(e) });
                handleTranslate?.(e, activeScript);
              }}
              loading={status === "loading" || isTranslateLoading}
            />
          </div>
        )}
      </div>

      {showType === 0 && (
        <>
          {/* 商品 */}
          {showProduct && (
            <div className="flex items-center gap-[8px] px-[16px] py-[16px]">
              <Image
                className="w-[32px] h-[32px] rounded-[4px] overflow-hidden"
                url={formatImgUrl("product", data[activeScript]?.product_id)}
                hoverEnlarge={true}
              ></Image>
              {data[activeScript]?.product_title ? (
                <div className="flex-1 line-clamp-1 text-kds-fg font-semibold">{data[activeScript]?.product_title}</div>
              ) : (
                <div className="text-[13px] text-kds-fg-subtle">
                  <Trans>No promotable products</Trans>
                </div>
              )}
            </div>
          )}
          {/* 内容区：脚本表格 */}
          <div className="flex-1 min-h-0">
            <StoryboardTable
              videoScripts={data[activeScript]?.video_scripts}
              isTranslate={data[activeScript]?.translate}
              scriptText={scriptText}
              isPlaying={isPlaying}
              handleCurTime={handleCurTime}
            />
          </div>
          {/* 底部操作栏 */}
          <div className="flex flex-row gap-[8px] items-center justify-end px-[24px] h-[72px] border-t border-kds-border">
            <SaveScriptBtn
              disabled={isTranslateLoading || status === "loading"}
              className="px-3 py-[5px]"
              data={data[activeScript]}
              showIndex={data.length > 1}
              activeScript={activeScript}
              handleSave={handleSave}
            />
            {location.pathname === "/insight" ? (
              <>
                <CommonButton
                  size="large"
                  disabled={isTranslateLoading || status === "loading"}
                  autoPath={false}
                  loading={downloadStatus === "loading"}
                  onClick={() => {
                    report.click({ kalo_type: "export_video_script", kalo_value: videoId });
                    downloadVideoScriptAnalysis({
                      translate: data[activeScript].translate,
                      activeScript: (activeScript % 2) + 1,
                      videoId: videoId || "",
                      videoSource: videoSource || "",
                    });
                  }}
                >
                  {renderExportScriptText()}
                </CommonButton>
                <CommonButton
                  size="large"
                  disabled={isTranslateLoading || status === "loading"}
                  type="primary"
                  onClick={() => {
                    report.click({
                      kalo_type: "script_imitation",
                    });
                    openWindow(
                      PAGES.AI_SCRIPT_REWRITE + `?id=${videoId}&videoSource=${videoSource}&country=${country}`,
                    );
                  }}
                >
                  <>
                    <Trans>Script Optimizer</Trans>
                    {data.length > 1 && <span>{activeScript + 1}</span>}
                  </>
                </CommonButton>
              </>
            ) : (
              <>
                <CommonButton
                  size="large"
                  loading={downloadStatus === "loading"}
                  disabled={isTranslateLoading || status === "loading"}
                  onClick={() => {
                    report.click({ kalo_type: "export_video_script", kalo_value: videoId });

                    downloadVideoScriptGenerate({
                      translate: data[activeScript].translate,
                      activeScript: (activeScript % 2) + 1,
                      requestIds: [data[activeScript].requestId],
                    });
                  }}
                >
                  {renderExportScriptText()}
                </CommonButton>
                <CreditGuardButton
                  size="large"
                  type="primary"
                  data-testid={TestId.VideoScriptGenerateButton}
                  disabled={isTranslateLoading || status === "loading"}
                  onClick={() => {
                    report.click({ kalo_type: "generate_video" });
                    setShowVideoCreationModal(true);
                  }}
                >
                  <Trans>Generate Video</Trans>
                </CreditGuardButton>
              </>
            )}
          </div>
        </>
      )}
      {showType === 1 && <VideoAnalysisPanel data={data[0]} isTrans={data[0].translate} />}

      <VideoCreationModal
        activeScript={activeScript}
        key={data[activeScript]?.requestId}
        open={showVideoCreationModal}
        onClose={() => setShowVideoCreationModal(false)}
        productItem={{
          id: data[activeScript]?.product_id,
          productTitle: data[activeScript]?.product_title,
          productCategory: data[activeScript]?.product_category,
          country: data[activeScript]?.country,
        }}
        requestId={data[activeScript]?.requestId}
        scriptLanguage={data[activeScript]?.language}
        onParseProductFailed={onParseProductFailed}
      />
    </div>
  );
};
